import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { SiteLocationTreeNode } from "~/services/paginatedHooks/useSiteLocationData";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

const SITE_LOCATION_TARGET_KIND = "siteLocation";

const BIN_LOCATION_TARGET_KIND = "binLocation";

const CAPACITY_LOCATIONS_PATH = "/produce/capacity/locations";

const CAPACITY_LOCATIONS_DOCK_PAGE_ID = "capacity-locations";

const EXACT_LOCATION_ROUTE_PATTERN = /^\/produce\/capacity\/locations\/sites\/([^/?#]+)(?:\/bins\/([^/?#]+))?$/;

export interface ExactLocation {
	locationId?: string;
	siteLocationId: string;
}

const parseExactLocationRoute = (pathname: string): ExactLocation | null => {
	const match = EXACT_LOCATION_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		const siteLocationId = decodeURIComponent(match[1]);
		return match[2] ? { locationId: decodeURIComponent(match[2]), siteLocationId } : { siteLocationId };
	} catch {
		return null;
	}
};

const exactLocationTarget = ({ locationId, siteLocationId }: ExactLocation): ContinuationTarget =>
	locationId
		? { listFilters: { siteLocationId }, targetId: locationId, targetKind: BIN_LOCATION_TARGET_KIND }
		: { targetId: siteLocationId, targetKind: SITE_LOCATION_TARGET_KIND };

const matchLocationReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const exactLocation = parseExactLocationRoute(pathname);
	return exactLocation ? exactLocationTarget(exactLocation) : null;
};

const exactLocationOf = (target: ContinuationTarget): ExactLocation | null => {
	if (target.targetKind === SITE_LOCATION_TARGET_KIND) return { siteLocationId: target.targetId };
	const siteLocationId = target.listFilters?.siteLocationId;
	return target.targetKind === BIN_LOCATION_TARGET_KIND && siteLocationId
		? { locationId: target.targetId, siteLocationId }
		: null;
};

const findExactLocationNode = (
	treeData: readonly SiteLocationTreeNode[],
	{ locationId, siteLocationId }: ExactLocation
): SiteLocationTreeNode | undefined =>
	treeData.find((node) =>
		locationId
			? node.nodeType === "subLocation" && node.locationId === locationId && node.siteLocationId === siteLocationId
			: node.nodeType === "siteLocation" && node.siteLocationId === siteLocationId
	);

const locationReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => {
		const exactLocation = exactLocationOf(target);
		if (!exactLocation) return "";
		const sitePath = `${CAPACITY_LOCATIONS_PATH}/sites/${encodeURIComponent(exactLocation.siteLocationId)}`;
		return exactLocation.locationId ? `${sitePath}/bins/${encodeURIComponent(exactLocation.locationId)}` : sitePath;
	},
	matchesRoute: matchLocationReceiverRoute
};

const LOCATION_TARGET_KINDS = [SITE_LOCATION_TARGET_KIND, BIN_LOCATION_TARGET_KIND];

const locationReceiverIdentity = {
	adapter: locationReceiverIdentityAdapter,
	BIN_LOCATION_TARGET_KIND,
	CAPACITY_LOCATIONS_DOCK_PAGE_ID,
	exactLocationTarget,
	findExactLocationNode,
	LOCATION_TARGET_KINDS,
	matchLocationReceiverRoute,
	parseExactLocationRoute,
	SITE_LOCATION_TARGET_KIND
} as const;

export default locationReceiverIdentity;
